import StepPanel from 'components/v2/compiler/StepPanel';
import { CompilerStepState } from 'components/v2/compiler/CompilerStepsValidator';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import EpdRoutes from 'routes/EpdRoutes';
import styled from 'styled-components';
import DynamicContentRenderer from '../DynamicContentRenderer';
import { DocumentNavContext } from '../hooks/useDocumentNav';
import { StepRecord } from 'types/dynamic-forms/records/StepRecord';
import { Container } from 'styles/Styles.styled';
import { WizardHeaderButtonBox, WizardHeaderGrid } from 'styles/v2/Styles.styled';
import TitleAndBack from 'components/v2/TitleAndBack';
import DocumentButtons from '../components/DocumentButtons';
import useProperty from '../hooks/useProperty';
import DocumentStatusBox from '../components/DocumentStatusBox';
import { useDocumentStore } from '../hooks/useDocumentStore';
import { getStepValidationState } from '../utils/stepValidation';

interface Props {
  steps: StepRecord[];
}

const RootRenderer: React.FC<Props> = ({ steps }) => {
  if (steps.length === 0) {
    return 'No steps in the schema';
  }

  const store = useDocumentStore();
  const [storeVersion, setStoreVersion] = useState(0);

  useEffect(() => {
    return store.addDataChangedListener(() => setStoreVersion((v) => v + 1));
  }, [store]);

  const match = useRouteMatch<{ docId: string; stepId: string }>({ path: '/dynamic-forms/:docId/:stepId*' });
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const tab = query.get('tab');
  const stepId = match ? match.params.stepId : undefined;
  const stepValidationStates = useMemo(
    () => steps.map((s) => getStepValidationState(s, store)),
    [steps, store, storeVersion]
  );

  const getUrl = (step: string, tab?: string | null) => {
    return `/dynamic-forms/${match?.params.docId}/${step}${tab ? '?tab=' + tab : ''}`;
  };

  if (!match || !stepId) {
    const defaultTab = steps[0].children?.[0]?.query;
    return <Redirect to={getUrl(steps[0].path, defaultTab)} />;
  }

  const currentStep = steps.find((s) => s.path === stepId);

  return (
    <DocumentNavContext.Provider
      value={{
        docId: match.params.docId,
        step: stepId,
        tab,
        getUrl,
      }}
    >
      <Container>
        <WizardHeaderGrid>
          <Title />
          <ToolButtonsContainer>
            <DocumentButtons />
          </ToolButtonsContainer>
        </WizardHeaderGrid>
        <WizardContentsGrid>
          <div>
            <DocumentStatusBox />

            <StepsPanelContainer>
              {steps.map((s, i) => (
                <StepPanel
                  key={s.name}
                  path={getUrl(s.path, s.children?.[0]?.query)}
                  label={`Step ${i + 1}`}
                  name={s.name}
                  description={s.description}
                  isSelected={s === currentStep}
                  stepState={validationToCompilerStepState(stepValidationStates[i])}
                />
              ))}
            </StepsPanelContainer>
          </div>

          <MainViewFull>{currentStep && <DynamicContentRenderer schema={currentStep} />}</MainViewFull>
        </WizardContentsGrid>
      </Container>
    </DocumentNavContext.Provider>
  );
};

export default RootRenderer;

const Title = () => {
  // Isolated in its own component, otherwise the entire tree will rerender when the property updates
  const epdTitle = useProperty(['title']);
  const history = useHistory();
  const onGoBack = () => history.push(EpdRoutes.dashboard);
  return <TitleAndBack title={epdTitle.value || 'Unnamed EPD'} backTo={'Back to the dashboard'} onBack={onGoBack} />;
};

const WizardContentsGrid = styled.div`
  display: grid;
  width: 100%;
  margin-bottom: 40px;
  align-items: start;
  grid-template-columns: 1fr;
  grid-column-gap: 2rem;
  border: 2 solid green;

  ${(props) => props.theme.media.tablet} {
    grid-template-columns: 3fr 10fr;
  }
`;

const ToolButtonsContainer = styled(WizardHeaderButtonBox)`
  min-height: 48px;
`;

const StepsPanelContainer = styled.div`
  padding: 0;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.04);
`;

const MainViewFull = styled.div`
  flex: 0 0 100%;
`;

function validationToCompilerStepState(valid: boolean | undefined): CompilerStepState | undefined {
  if (valid === undefined) {
    return undefined;
  }
  return valid ? 'ok' : 'error';
}
